import { Box } from "@outschool/backpack";
import { faGlobe } from "@outschool/icons";
import {
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
  SupportedLocale,
  useTranslation
} from "@outschool/localization";
import {
  ConditionalTrackingPageSectionOrSubSection,
  Icon,
  useImpressionTracking
} from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import * as React from "react";

import { useLanguagePreference } from "../../hooks/Localization";
import Select, { SelectOption } from "../lib/Select";

export type LanguageOption = SelectOption<SupportedLocale>;

type LanguageSelectorProps = {
  selectedLanguage: SupportedLocale;
  setSelectedLanguage: (locale: SupportedLocale) => void;
};

export default function LanguageSelector({
  selectedLanguage,
  setSelectedLanguage
}: LanguageSelectorProps) {
  const { t } = useTranslation(
    "client\\components\\localization\\LanguageSelector"
  );
  const [menuTrackingNode, setMenuTrackingNode] = React.useState<HTMLElement>();
  const { trackTouch } = useImpressionTracking({
    node: menuTrackingNode,
    uniqueId: "language-selector",
    trackingLabel: "languageSelector"
  });
  const { browserLanguages } = useLanguagePreference();

  const makeLanguageOption = ({
    locale,
    localLanguageName
  }: SupportedLanguage): LanguageOption => {
    return {
      value: locale,
      label: localLanguageName
    };
  };

  const findOption = (locale: SupportedLocale): SupportedLanguage => {
    // @ts-ignore TS(2322): Type 'SupportedLanguage | undefined' is not assign... Remove this comment to see the full error message
    return SUPPORTED_LANGUAGES.find(lang => lang.locale == locale);
  };

  const onSelectHandler = (newValue: SupportedLocale) => {
    if (newValue) {
      const option = findOption(newValue);
      trackTouch({ newLanguage: newValue });
      setSelectedLanguageOption(makeLanguageOption(option));
    }
  };

  const languageOptions: LanguageOption[] = SUPPORTED_LANGUAGES.map(
    languageInfo => makeLanguageOption(languageInfo)
  );

  const languagePreferences = _.fromPairs(
    browserLanguages.map((language, idx) => [
      `language_preference_${idx}`,
      language
    ])
  );

  const initialLanguageInfo = findOption(selectedLanguage);

  const [selectedLanguageOption, setSelectedLanguageOption] = React.useState(
    makeLanguageOption(initialLanguageInfo)
  );

  React.useEffect(() => {
    setSelectedLanguage(selectedLanguageOption.value);
  }, [setSelectedLanguage, selectedLanguageOption]);

  return (
    <ConditionalTrackingPageSectionOrSubSection
      name="language_selector_section"
      uniqueId="language_selector"
      // @ts-ignore TS(2322): Type 'Dictionary<string | undefined>' is not assig... Remove this comment to see the full error message
      sharedProperties={languagePreferences}
    >
      {trackingNode => (
        <Box flex>
          <Box
            ref={trackingNode}
            data-test-id="activity-description-language-selector"
            sx={{
              position: "relative",
              flex: "auto"
            }}
          >
            <Select
              options={languageOptions}
              value={selectedLanguageOption.value}
              handleChangeOnBlur={false}
              onChange={onSelectHandler}
              sx={{ display: "block", paddingLeft: "22px" }}
              placeholder={t`Select a language...`}
              // @ts-ignore TS(2322): Type 'Dispatch<SetStateAction<HTMLElement | undefi... Remove this comment to see the full error message
              ref={setMenuTrackingNode}
            />
            <Icon
              aria-hidden
              icon={faGlobe}
              sx={{
                position: "absolute",
                left: 15,
                top: "13px",
                color: "peacock500"
              }}
            />
          </Box>
        </Box>
      )}
    </ConditionalTrackingPageSectionOrSubSection>
  );
}
